import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Timestamp, doc, updateDoc, collection, addDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebase-config';
import { TimeAdjustmentModel } from '../../models/TimeEntryModels';
import '../../styles/staff-forms-modals.css';

const TimeEntryEditModal = ({
  isOpen,
  onClose,
  timeEntry,
  companyId,
  staffId,
  staffName,
  onSave,
  onDelete,
  isAddMode = false
}) => {
  const { t } = useTranslation(['staff']);

  // Ref for scrolling to break section after adding a new break
  const breakSectionRef = useRef(null);

  const isCreatingNew = isAddMode || !timeEntry;

  const [departments, setDepartments] = useState([]);

  const [formData, setFormData] = useState({
    clockIns: [],
    clockOuts: [],
    breakStarts: [],
    breakEnds: [],
    date: '',
    departmentId: '',
    departmentName: '',
    note: '',
    sessionStatus: '',
    breakComplianceAdjusted: false,
    breakComplianceAdjustment: 0,
    breakComplianceAdjustmentTimestamp: null
  });

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // NEW: replaces window.confirm() for unsaved changes
  const [showUnsavedChangesConfirm, setShowUnsavedChangesConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isCreatingNew && companyId && isOpen) {
      loadDepartments();
    }
  }, [isCreatingNew, companyId, isOpen]);

  const loadDepartments = async () => {
    try {
      const departmentsRef = collection(db, 'companies', companyId, 'departments');
      const snapshot = await getDocs(departmentsRef);
      const deptList = [];
      snapshot.forEach(doc => {
        deptList.push({
          id: doc.id,
          name: doc.data().name || 'Unnamed Department'
        });
      });
      setDepartments(deptList);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isCreatingNew) {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        const initialData = {
          clockIns: [`${todayString}T09:00`],
          clockOuts: [`${todayString}T17:00`],
          breakStarts: [],
          breakEnds: [],
          date: todayString,
          departmentId: '',
          departmentName: '',
          note: '',
          sessionStatus: 'Clocked Out',
          breakComplianceAdjusted: false,
          breakComplianceAdjustment: 0,
          breakComplianceAdjustmentTimestamp: null
        };
        setFormData(initialData);
        setHasChanges(true);
      } else if (timeEntry) {
        const initialData = {
          clockIns: timeEntry.clockIns?.map(ts => formatTimestampForInput(ts)) || [],
          clockOuts: timeEntry.clockOuts?.map(ts => formatTimestampForInput(ts)) || [],
          breakStarts: timeEntry.breakStarts?.map(ts => formatTimestampForInput(ts)) || [],
          breakEnds: timeEntry.breakEnds?.map(ts => formatTimestampForInput(ts)) || [],
          date: timeEntry.date || '',
          departmentId: timeEntry.departmentId || '',
          departmentName: timeEntry.departmentName || '',
          note: timeEntry.note || '',
          sessionStatus: timeEntry.sessionStatus || 'Clocked Out',
          breakComplianceAdjusted: timeEntry.breakComplianceAdjusted || false,
          breakComplianceAdjustment: timeEntry.breakComplianceAdjustment || 0,
          breakComplianceAdjustmentTimestamp: timeEntry.breakComplianceAdjustmentTimestamp || null
        };
        setFormData(initialData);
        setHasChanges(false);
      }
      setError(null);
      setValidationErrors({});
      setShowDeleteConfirm(false);
      // NEW: reset unsaved changes dialog when modal opens
      setShowUnsavedChangesConfirm(false);
    }
  }, [timeEntry, isOpen, isCreatingNew]);

  useEffect(() => {
    if (!isCreatingNew && timeEntry && isOpen) {
      const originalData = {
        clockIns: timeEntry.clockIns?.map(ts => formatTimestampForInput(ts)) || [],
        clockOuts: timeEntry.clockOuts?.map(ts => formatTimestampForInput(ts)) || [],
        breakStarts: timeEntry.breakStarts?.map(ts => formatTimestampForInput(ts)) || [],
        breakEnds: timeEntry.breakEnds?.map(ts => formatTimestampForInput(ts)) || [],
        note: timeEntry.note || '',
        departmentId: timeEntry.departmentId || ''
      };
      const hasDataChanged = JSON.stringify(originalData) !== JSON.stringify({
        clockIns: formData.clockIns,
        clockOuts: formData.clockOuts,
        breakStarts: formData.breakStarts,
        breakEnds: formData.breakEnds,
        note: formData.note,
        departmentId: formData.departmentId
      });
      setHasChanges(hasDataChanged);
    }
  }, [formData, timeEntry, isOpen, isCreatingNew]);

  const calculatedDurations = useMemo(() => {
    return calculateDurations(formData, formData.date, isCreatingNew ? null : timeEntry);
  }, [formData, isCreatingNew, timeEntry]);

  const handleArrayFieldChange = (fieldName, index, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].map((item, i) => i === index ? value : item)
    }));
    if (validationErrors[`${fieldName}[${index}]`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${fieldName}[${index}]`];
        return newErrors;
      });
    }
  };

  const handleSimpleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleDepartmentChange = (departmentId) => {
    const selectedDept = departments.find(d => d.id === departmentId);
    setFormData(prev => ({
      ...prev,
      departmentId,
      departmentName: selectedDept?.name || ''
    }));
  };

  const addTimestampEntry = (fieldName) => {
    const currentDate = formData.date || new Date().toISOString().split('T')[0];
    let defaultTime = '09:00';
    if (formData[fieldName].length > 0) {
      const lastEntry = formData[fieldName][formData[fieldName].length - 1];
      const lastTime = new Date(lastEntry);
      const newTime = new Date(
        lastTime.getTime() +
        (fieldName.includes('Out') || fieldName.includes('End') ? 60 * 60 * 1000 : 30 * 60 * 1000)
      );
      defaultTime = newTime.toTimeString().slice(0, 5);
    } else {
      switch (fieldName) {
        case 'clockIns':    defaultTime = '09:00'; break;
        case 'clockOuts':   defaultTime = '17:00'; break;
        case 'breakStarts': defaultTime = '12:00'; break;
        case 'breakEnds':   defaultTime = '13:00'; break;
        default: break;
      }
    }
    setFormData(prev => ({
      ...prev,
      [fieldName]: [...prev[fieldName], `${currentDate}T${defaultTime}`]
    }));
  };

  const removeTimestampEntry = (fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fieldName}[${index}]`];
      return newErrors;
    });
  };

  const validateForm = () => {
    const errors = {};

    if (formData.clockIns.length === 0) {
      errors.clockIns = t('staff:timeManagement.timeEntry.validation.clockInRequired');
    }

    if (isCreatingNew) {
      if (!formData.date) {
        errors.date = t('staff:timeManagement.timeEntry.validation.dateRequired');
      }
      if (!formData.departmentId) {
        errors.departmentId = t('staff:timeManagement.timeEntry.validation.departmentRequired');
      }
    }

    const validateTimestampArray = (timestamps, fieldName) => {
      timestamps.forEach((timestamp, index) => {
        if (!timestamp) {
          errors[`${fieldName}[${index}]`] = t('staff:timeManagement.timeEntry.validation.timeRequired');
        } else if (!isValidTimestamp(timestamp)) {
          errors[`${fieldName}[${index}]`] = t('staff:timeManagement.timeEntry.validation.invalidTimeFormat');
        }
      });
    };

    validateTimestampArray(formData.clockIns, 'clockIns');
    validateTimestampArray(formData.clockOuts, 'clockOuts');
    validateTimestampArray(formData.breakStarts, 'breakStarts');
    validateTimestampArray(formData.breakEnds, 'breakEnds');

    if (formData.breakStarts.length !== formData.breakEnds.length) {
      errors.breaks = t('staff:timeManagement.timeEntry.validation.breaksMismatch');
    }

    const clockIns = formData.clockIns.map(ts => new Date(ts)).filter(d => !isNaN(d));
    const clockOuts = formData.clockOuts.map(ts => new Date(ts)).filter(d => !isNaN(d));

    for (let i = 0; i < Math.min(clockIns.length, clockOuts.length); i++) {
      if (clockOuts[i] <= clockIns[i]) {
        errors[`clockOuts[${i}]`] = t('staff:timeManagement.timeEntry.validation.clockOutBeforeIn');
      }
    }

    const breakStarts = formData.breakStarts.map(ts => new Date(ts)).filter(d => !isNaN(d));
    const breakEnds = formData.breakEnds.map(ts => new Date(ts)).filter(d => !isNaN(d));

    for (let i = 0; i < Math.min(breakStarts.length, breakEnds.length); i++) {
      if (breakEnds[i] <= breakStarts[i]) {
        errors[`breakEnds[${i}]`] = t('staff:timeManagement.timeEntry.validation.breakEndBeforeStart');
      }
    }

    for (let i = 0; i < breakStarts.length; i++) {
      let breakInWorkSession = false;
      for (let j = 0; j < Math.min(clockIns.length, clockOuts.length); j++) {
        if (breakStarts[i] >= clockIns[j] && breakEnds[i] <= clockOuts[j]) {
          breakInWorkSession = true;
          break;
        }
      }
      if (!breakInWorkSession && clockIns.length > 0 && clockOuts.length > 0) {
        errors[`breakStarts[${i}]`] = t('staff:timeManagement.timeEntry.validation.breakOutsideSession');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDeleteEntry = async () => {
    if (!timeEntry || isCreatingNew) return;
    setDeleting(true);
    setError(null);
    try {
      const entryRef = doc(db, 'companies', companyId, 'timeEntries', staffId, 'entries', timeEntry.id);
      await deleteDoc(entryRef);

      const adjustmentData = TimeAdjustmentModel.createForEdit(
        companyId,
        staffId,
        timeEntry.id,
        timeEntry,
        {},
        formData.note.trim() || 'Time entry deleted by admin',
        'admin'
      );
      adjustmentData.adjustmentType = 'DELETION';

      const adjustmentsRef = collection(db, 'companies', companyId, 'timeAdjustments');
      await addDoc(adjustmentsRef, adjustmentData.toFirestoreData());

      if (onDelete) onDelete(timeEntry);
      onClose();
    } catch (err) {
      console.error('Error deleting time entry:', err);
      setError(t('staff:timeManagement.errors.failedToDeleteTimeEntry'));
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setError(t('staff:timeManagement.errors.validationErrors'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const clockIns    = formData.clockIns.map(ts => Timestamp.fromDate(new Date(ts)));
      const clockOuts   = formData.clockOuts.map(ts => Timestamp.fromDate(new Date(ts)));
      const breakStarts = formData.breakStarts.map(ts => Timestamp.fromDate(new Date(ts)));
      const breakEnds   = formData.breakEnds.map(ts => Timestamp.fromDate(new Date(ts)));

      let sessionStatus = formData.sessionStatus;
      if (clockIns.length > clockOuts.length) {
        sessionStatus = 'Working';
      } else if (clockIns.length === clockOuts.length && clockOuts.length > 0) {
        sessionStatus = 'Clocked Out';
      } else {
        sessionStatus = 'Unknown';
      }

      const entryData = {
        departmentId: formData.departmentId,
        date: formData.date,
        clockIns,
        clockOuts,
        breakStarts,
        breakEnds,
        totalWorkDuration: calculatedDurations.workDuration,
        totalBreakDuration: calculatedDurations.breakDuration,
        sessionStatus,
        deviceType: 'Web',
        deviceId: 'admin',
        note: formData.note.trim(),
        lastModified: Timestamp.now(),
        modifiedBy: 'admin'
      };

      let resultEntry;

      if (isCreatingNew) {
        entryData.createdAt = Timestamp.now();
        entryData.createdBy = 'admin';
        const entriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
        const docRef = await addDoc(entriesRef, entryData);
        resultEntry = {
          id: docRef.id,
          staffId,
          departmentName: formData.departmentName,
          ...entryData,
          totalWorkDuration: Math.round(calculatedDurations.workDuration / 60),
          totalBreakDuration: Math.round(calculatedDurations.breakDuration / 60),
          isNewEntry: true
        };
      } else {
        entryData.breakComplianceAdjusted = formData.breakComplianceAdjusted;
        entryData.breakComplianceAdjustment = formData.breakComplianceAdjustment;
        entryData.breakComplianceAdjustmentTimestamp = formData.breakComplianceAdjustmentTimestamp;
        const entryRef = doc(db, 'companies', companyId, 'timeEntries', staffId, 'entries', timeEntry.id);
        await updateDoc(entryRef, entryData);
        resultEntry = {
          ...timeEntry,
          ...entryData,
          totalWorkDuration: Math.round(calculatedDurations.workDuration / 60),
          totalBreakDuration: Math.round(calculatedDurations.breakDuration / 60)
        };
      }

      if (onSave) await onSave(resultEntry);
      onClose();
    } catch (err) {
      console.error('Error saving time entry:', err);
      setError(
        isCreatingNew
          ? t('staff:timeManagement.errors.failedToCreate')
          : t('staff:timeManagement.errors.failedToUpdateTimeEntry')
      );
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: no longer calls window.confirm() — shows custom dialog instead
  const handleCancel = () => {
    if (hasChanges) {
      setShowUnsavedChangesConfirm(true);
    } else {
      resetForm();
      onClose();
    }
  };

  // NEW: called when the user confirms they want to discard changes
  const confirmDiscard = () => {
    setShowUnsavedChangesConfirm(false);
    resetForm();
    onClose();
  };

  // UPDATED: also resets the unsaved changes dialog state
  const resetForm = () => {
    setError(null);
    setValidationErrors({});
    setHasChanges(false);
    setShowDeleteConfirm(false);
    setShowUnsavedChangesConfirm(false);
  };

  if (!isOpen) return null;
  if (!isCreatingNew && !timeEntry) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content time-entry-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>
              {isCreatingNew
                ? t('staff:timeManagement.timeEntry.add.title')
                : t('staff:timeManagement.timeEntry.edit.title')
              }
            </h2>
            <p className="modal-subtitle">
              <span className="staff-name">{staffName}</span>
              {!isCreatingNew && (
                <>
                  <span className="separator">•</span>
                  <span className="date">{formatDateHeader(timeEntry.date)}</span>
                  <span className="separator">•</span>
                  <span className="department">{formData.departmentName}</span>
                </>
              )}
            </p>
          </div>
          <div className="modal-header-actions">
            {!isCreatingNew && timeEntry && (
              <button
                className="delete-entry-button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading || deleting}
                title={t('staff:timeManagement.timeEntry.delete.buttonTitle')}
              >
                <span className="icon-trash">×</span>
                {t('staff:timeManagement.timeEntry.delete.buttonLabel')}
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleCancel}
              title={t('staff:timeManagement.timeEntry.delete.closeTitle')}
            >
              <span className="icon-close">×</span>
            </button>
          </div>
        </div>

        {/* Global error banner */}
        {error && (
          <div className="modal-error">
            <span className="icon-error">⚠</span>
            {error}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="confirmation-overlay">
            <div className="confirmation-dialog delete-confirmation">
              <div className="confirmation-header">
                <span className="icon-warning">⚠</span>
                <h3>{t('staff:timeManagement.timeEntry.delete.confirmTitle')}</h3>
              </div>
              <div className="confirmation-body">
                <p>
                  {t('staff:timeManagement.timeEntry.delete.message', {
                    staffName,
                    date: formatDateHeader(timeEntry.date)
                  })}
                </p>
                <div className="entry-summary">
                  <div className="summary-item">
                    <strong>{t('staff:timeManagement.timeEntry.delete.workTime')}:</strong>
                    {formatDurationDisplay(calculatedDurations.workDuration)}
                  </div>
                  <div className="summary-item">
                    <strong>{t('staff:timeManagement.timeEntry.delete.breakTime')}:</strong>
                    {formatDurationDisplay(calculatedDurations.breakDuration)}
                  </div>
                  <div className="summary-item">
                    <strong>{t('staff:timeManagement.timeEntry.delete.department')}:</strong>
                    {formData.departmentName}
                  </div>
                </div>
                <p className="warning-text">
                  {t('staff:timeManagement.timeEntry.delete.warning')}
                </p>
              </div>
              <div className="confirmation-actions">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="cancel-button-secondary"
                  disabled={deleting}
                >
                  {t('staff:buttons.cancel')}
                </button>
                <button
                  onClick={handleDeleteEntry}
                  className="delete-button-confirm"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span className="spinner small" />
                      {t('staff:buttons.deleting')}
                    </>
                  ) : (
                    <>
                      <span className="icon-trash">×</span>
                      {t('staff:buttons.deleteEntry')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unsaved Changes Confirmation Dialog */}
{showUnsavedChangesConfirm && (
  <div className="confirmation-overlay">
    <div className="confirmation-dialog unsaved-changes-confirmation">
      <div className="confirmation-header">
        <span className="icon-warning">⚠</span>

        {/* CHANGE THIS LINE */}
        <h3>{t('staff:timeManagement.timeEntry.unsavedChangesTitle')}</h3>

      </div>
      <div className="confirmation-body">
        <p>{t('staff:timeManagement.timeEntry.unsavedChangesConfirm')}</p>
      </div>
      <div className="confirmation-actions">
        <button
          onClick={() => setShowUnsavedChangesConfirm(false)}
          className="cancel-button-secondary"
        >
          {t('staff:buttons.cancel')}
        </button>
        <button
          onClick={confirmDiscard}
          className="discard-button-confirm"
        >
          {/* CHANGE THIS LINE */}
          {t('staff:timeManagement.timeEntry.unsavedChangesDiscard')}

        </button>
      </div>
    </div>
  </div>
)}

        {/* Modal Body */}
        <div className="modal-body">

          {/* Basic Info — add mode only */}
          {isCreatingNew && (
            <div className="form-section basic-info">
              <h3>
                <span className="icon-info">i</span>
                {t('staff:timeManagement.timeEntry.add.basicInfo.title')}
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="entry-date">
                    {t('staff:timeManagement.timeEntry.add.basicInfo.date')}*
                  </label>
                  <input
                    id="entry-date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        date: newDate,
                        clockIns:    prev.clockIns.map(ts => `${newDate}T${ts.split('T')[1] || '09:00'}`),
                        clockOuts:   prev.clockOuts.map(ts => `${newDate}T${ts.split('T')[1] || '17:00'}`),
                        breakStarts: prev.breakStarts.map(ts => `${newDate}T${ts.split('T')[1] || '12:00'}`),
                        breakEnds:   prev.breakEnds.map(ts => `${newDate}T${ts.split('T')[1] || '13:00'}`)
                      }));
                    }}
                    className={validationErrors.date ? 'error' : ''}
                  />
                  {validationErrors.date && (
                    <div className="field-error">{validationErrors.date}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="entry-department">
                    {t('staff:timeManagement.timeEntry.add.basicInfo.department')}*
                  </label>
                  <select
                    id="entry-department"
                    value={formData.departmentId}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    className={validationErrors.departmentId ? 'error' : ''}
                  >
                    <option value="">
                      {t('staff:timeManagement.timeEntry.add.basicInfo.selectDepartment')}
                    </option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  {validationErrors.departmentId && (
                    <div className="field-error">{validationErrors.departmentId}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Duration Preview */}
          <div className="duration-preview">
            <h3>{t('staff:timeManagement.timeEntry.edit.calculatedDurations')}</h3>
            <div className="duration-summary">
              <div className="duration-item work">
                <div className="duration-value">{formatDurationDisplay(calculatedDurations.workDuration)}</div>
                <div className="duration-label">{t('staff:timeManagement.timeEntry.edit.workTime')}</div>
              </div>
              <div className="duration-item break">
                <div className="duration-value">{formatDurationDisplay(calculatedDurations.breakDuration)}</div>
                <div className="duration-label">{t('staff:timeManagement.timeEntry.edit.breakTime')}</div>
              </div>
              <div className="duration-item total">
                <div className="duration-value">{formatDurationDisplay(calculatedDurations.totalTime)}</div>
                <div className="duration-label">{t('staff:timeManagement.timeEntry.edit.totalTime')}</div>
              </div>
            </div>
          </div>

          {/* Clock In Times */}
          <div className="form-section">
            <div className="section-header">
              <h3>
                <span className="icon-login" />
                {t('staff:timeManagement.timeEntry.edit.clockInTimes')}
              </h3>
              <button
                type="button"
                onClick={() => addTimestampEntry('clockIns')}
                className="add-button"
              >
                <span className="icon-plus">+</span> {t('staff:timeManagement.timeEntry.edit.addClockIn')}
              </button>
            </div>
            {validationErrors.clockIns && (
              <div className="section-error">{validationErrors.clockIns}</div>
            )}
            <div className="timestamp-list">
              {formData.clockIns.map((clockIn, index) => (
                <div key={index} className="timestamp-entry">
                  <div className="timestamp-input-group">
                    <label>#{index + 1}</label>
                    <input
                      type="datetime-local"
                      value={clockIn}
                      onChange={(e) => handleArrayFieldChange('clockIns', index, e.target.value)}
                      className={validationErrors[`clockIns[${index}]`] ? 'error' : ''}
                    />
                    {formData.clockIns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTimestampEntry('clockIns', index)}
                        className="remove-button"
                      >
                        <span className="icon-trash">×</span>
                      </button>
                    )}
                  </div>
                  {validationErrors[`clockIns[${index}]`] && (
                    <div className="field-error">{validationErrors[`clockIns[${index}]`]}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Clock Out Times */}
          <div className="form-section">
            <div className="section-header">
              <h3>
                <span className="icon-logout">&#8593;</span>
                {t('staff:timeManagement.timeEntry.edit.clockOutTimes')}
              </h3>
              <button
                type="button"
                onClick={() => addTimestampEntry('clockOuts')}
                className="add-button"
              >
                <span className="icon-plus">+</span> {t('staff:timeManagement.timeEntry.edit.addClockOut')}
              </button>
            </div>
            <div className="timestamp-list">
              {formData.clockOuts.map((clockOut, index) => (
                <div key={index} className="timestamp-entry">
                  <div className="timestamp-input-group">
                    <label>#{index + 1}</label>
                    <input
                      type="datetime-local"
                      value={clockOut}
                      onChange={(e) => handleArrayFieldChange('clockOuts', index, e.target.value)}
                      className={validationErrors[`clockOuts[${index}]`] ? 'error' : ''}
                    />
                    <button
                      type="button"
                      onClick={() => removeTimestampEntry('clockOuts', index)}
                      className="remove-button"
                    >
                      <span className="icon-trash">×</span>
                    </button>
                  </div>
                  {validationErrors[`clockOuts[${index}]`] && (
                    <div className="field-error">{validationErrors[`clockOuts[${index}]`]}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Break Times — ref attached here for scroll-to-view */}
          <div className="form-section" ref={breakSectionRef}>
            <div className="section-header">
              <h3>
                <span className="icon-break">||</span>
                {t('staff:timeManagement.timeEntry.edit.breakPeriods')}
              </h3>
              <button
                type="button"
                onClick={() => {
                  addTimestampEntry('breakStarts');
                  addTimestampEntry('breakEnds');
                  setTimeout(() => {
                    breakSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                  }, 100);
                }}
                className="add-button"
              >
                <span className="icon-plus">+</span> {t('staff:timeManagement.timeEntry.edit.addBreakPeriod')}
              </button>
            </div>
            {validationErrors.breaks && (
              <div className="section-error">{validationErrors.breaks}</div>
            )}

            {Math.max(formData.breakStarts.length, formData.breakEnds.length) > 0 ? (
              <div className="break-pairs">
                {Array.from(
                  { length: Math.max(formData.breakStarts.length, formData.breakEnds.length) },
                  (_, index) => (
                    <div key={index} className="break-pair">
                      <div className="break-pair-header">
                        <span className="break-number">
                          {t('staff:timeManagement.timeEntry.edit.breakNumber', { number: index + 1 })}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            removeTimestampEntry('breakStarts', index);
                            removeTimestampEntry('breakEnds', index);
                          }}
                          className="remove-button small"
                        >
                          <span className="icon-trash">×</span>{' '}
                          {t('staff:buttons.remove')}
                        </button>
                      </div>

                      <div className="break-times">
                        <div className="break-start">
                          <label>{t('staff:timeManagement.timeEntry.edit.breakStart')}</label>
                          <input
                            type="datetime-local"
                            value={formData.breakStarts[index] || ''}
                            onChange={(e) => {
                              if (index >= formData.breakStarts.length) {
                                setFormData(prev => ({
                                  ...prev,
                                  breakStarts: [...prev.breakStarts, e.target.value]
                                }));
                              } else {
                                handleArrayFieldChange('breakStarts', index, e.target.value);
                              }
                            }}
                            className={validationErrors[`breakStarts[${index}]`] ? 'error' : ''}
                          />
                          {validationErrors[`breakStarts[${index}]`] && (
                            <div className="field-error">{validationErrors[`breakStarts[${index}]`]}</div>
                          )}
                        </div>

                        <div className="break-end">
                          <label>{t('staff:timeManagement.timeEntry.edit.breakEnd')}</label>
                          <input
                            type="datetime-local"
                            value={formData.breakEnds[index] || ''}
                            onChange={(e) => {
                              if (index >= formData.breakEnds.length) {
                                setFormData(prev => ({
                                  ...prev,
                                  breakEnds: [...prev.breakEnds, e.target.value]
                                }));
                              } else {
                                handleArrayFieldChange('breakEnds', index, e.target.value);
                              }
                            }}
                            className={validationErrors[`breakEnds[${index}]`] ? 'error' : ''}
                          />
                          {validationErrors[`breakEnds[${index}]`] && (
                            <div className="field-error">{validationErrors[`breakEnds[${index}]`]}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="empty-breaks">
                <p>{t('staff:timeManagement.timeEntry.edit.noBreakPeriods')}</p>
              </div>
            )}
          </div>

          {/* Automatic Break Info — existing entries only */}
          {!isCreatingNew && timeEntry?.breakComplianceAdjusted && timeEntry?.breakComplianceAdjustment > 0 && (
            <div className="form-section automatic-break-info">
              <h3>
                <span className="icon-info">i</span>
                {t('staff:timeManagement.timeEntry.edit.automaticBreakAdjustment')}
              </h3>
              <div className="auto-break-details">
                <p className="info-text">
                  {t('staff:timeManagement.timeEntry.edit.automaticBreakDescription')}
                </p>
                <div className="auto-break-value">
                  <strong>
                    {t('staff:timeManagement.timeEntry.edit.automaticBreakTime')}:{' '}
                    {Math.round(timeEntry.breakComplianceAdjustment)}{' '}
                    {t('staff:timeManagement.timeEntry.edit.minutes')}
                  </strong>
                </div>
                {timeEntry.breakComplianceAdjustmentTimestamp && (
                  <div className="auto-break-timestamp">
                    {t('staff:timeManagement.timeEntry.edit.applied')}:{' '}
                    {new Date(timeEntry.breakComplianceAdjustmentTimestamp.toDate()).toLocaleString()}
                  </div>
                )}
                <div className="auto-break-note">
                  <em>{t('staff:timeManagement.timeEntry.edit.adjustmentPreserved')}</em>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="form-section">
            <h3>
              <span className="icon-note" />
              {t('staff:timeManagement.timeEntry.notes.title')}
            </h3>
            <textarea
              value={formData.note}
              onChange={(e) => handleSimpleFieldChange('note', e.target.value)}
              placeholder={
                isCreatingNew
                  ? t('staff:timeManagement.timeEntry.notes.addPlaceholder')
                  : t('staff:timeManagement.timeEntry.notes.editPlaceholder')
              }
              rows={3}
              className="note-textarea"
              maxLength={500}
            />
            <div className="character-count">
              {t('staff:timeManagement.timeEntry.notes.characterCount', { count: formData.note.length })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-info">
            {hasChanges && (
              <span className="unsaved-indicator">
                • {t('staff:timeManagement.timeEntry.unsavedChanges')}
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button
              onClick={handleCancel}
              className="cancel-button-professional"
              disabled={loading || deleting}
            >
              {t('staff:buttons.cancel')}
            </button>
            <button
              onClick={handleSave}
              className={`save-button ${hasChanges ? 'has-changes' : ''}`}
              disabled={loading || deleting || (!hasChanges && !isCreatingNew)}
            >
              {loading ? (
                <>
                  <span className="spinner small" />
                  {isCreatingNew
                    ? t('staff:buttons.creating')
                    : t('staff:buttons.saving')
                  }
                </>
              ) : (
                <>
                  <span className="icon-save" />
                  {isCreatingNew
                    ? t('staff:buttons.createEntry')
                    : t('staff:buttons.saveChanges')
                  }
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

// ─── Helper Functions ────────────────────────────────────────────────────────

const formatTimestampForInput = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const year    = date.getFullYear();
    const month   = String(date.getMonth() + 1).padStart(2, '0');
    const day     = String(date.getDate()).padStart(2, '0');
    const hours   = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

const formatDateHeader = (dateString) => {
  try {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false;
  const date = new Date(timestamp);
  return date instanceof Date && !isNaN(date);
};

const formatDurationDisplay = (seconds) => {
  if (!seconds || seconds < 0) return '0h 0m';
  const hours   = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const calculateDurations = (formData, entryDate, originalTimeEntry) => {
  try {
    const clockIns    = formData.clockIns.map(ts => new Date(ts)).filter(d => !isNaN(d));
    const clockOuts   = formData.clockOuts.map(ts => new Date(ts)).filter(d => !isNaN(d));
    const breakStarts = formData.breakStarts.map(ts => new Date(ts)).filter(d => !isNaN(d));
    const breakEnds   = formData.breakEnds.map(ts => new Date(ts)).filter(d => !isNaN(d));

    let totalSessionTime = 0;
    for (let i = 0; i < Math.min(clockIns.length, clockOuts.length); i++) {
      if (clockOuts[i] > clockIns[i]) {
        totalSessionTime += (clockOuts[i] - clockIns[i]) / 1000;
      }
    }

    let manualBreakDuration = 0;
    for (let j = 0; j < Math.min(breakStarts.length, breakEnds.length); j++) {
      if (breakEnds[j] > breakStarts[j]) {
        manualBreakDuration += (breakEnds[j] - breakStarts[j]) / 1000;
      }
    }

    let automaticBreakAdjustment = 0;
    if (originalTimeEntry?.breakComplianceAdjusted && originalTimeEntry?.breakComplianceAdjustment) {
      automaticBreakAdjustment = originalTimeEntry.breakComplianceAdjustment * 60;
    }

    const totalBreakDuration = manualBreakDuration + automaticBreakAdjustment;
    const totalWorkDuration  = Math.max(0, totalSessionTime - totalBreakDuration);

    return {
      workDuration:  Math.round(totalWorkDuration),
      breakDuration: Math.round(totalBreakDuration),
      totalTime:     Math.round(totalSessionTime)
    };
  } catch (error) {
    console.error('Error calculating durations:', error);
    return { workDuration: 0, breakDuration: 0, totalTime: 0 };
  }
};

export default TimeEntryEditModal;